import AppFormContainer from '@nxsys/components/AppFormContainer';
import { EeErPensionMethodEnum } from '@nxsys/constants/AppEnums';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import { getTypeOfValueOptions } from '@nxsys/constants/AppConstants';
import { useUpdateEmployeeIrelandPrivatePension } from '@nxsys/hooks/api/employee/useEmployeeApi';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IEmployeeIrelandPrivatePension } from '@nxsys/types/models/pension';
import { showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { Col, Descriptions, Form, Row, Tag } from 'antd';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';
import { privatePensionValueRule } from '@nxsys/utils/formFieldValidation';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  subcontractorId: string;
  data: IEmployeeIrelandPrivatePension;
  onSuccess: () => void;
};

const EmployeePrivatePensionEditModal: React.FC<Props> = ({
  isOpen,
  toggle,
  subcontractorId,
  data,
  onSuccess,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();
  const erPensionType = Form.useWatch('erPensionType', form);
  const eePensionType = Form.useWatch('eePensionType', form);
  const avcPensionType = Form.useWatch('avcPensionType', form);
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const pensionTypeOptions = getTypeOfValueOptions(
    showCurrencyByCountry(payrollCountry),
  );

  const { run: updateSetting, loading: updating } =
    useUpdateEmployeeIrelandPrivatePension();

  const handleSubmit = async (values: any) => {
    const res = await updateSetting(subcontractorId, data.id, {
      eePensionValue: Number(values.eePensionValue) || 0,
      eePensionType: values.eePensionType?.value || 'PERCENTAGE',
      erPensionValue: Number(values.erPensionValue) || 0,
      erPensionType: values.erPensionType?.value || 'PERCENTAGE',
      avcPensionValue: Number(values.avcPensionValue) || 0,
      avcPensionType: values.avcPensionType?.value || 'PERCENTAGE',
    });
    if (res) {
      showMessage({ type: 'success', content: messages['common.updateSuccess'] });
      toggle();
      onSuccess();
    }
  };

  const displayType = data.companyPensionSetting?.type || data.type || '-';
  const displayName = data.companyPensionSetting?.name || data.name || '-';
  const displayRef =
    data.companyPensionSetting?.reference || data.reference || '-';

  return (
    <AppModalContainer
      title={messages['common.editPensionScheme'] ?? 'Edit Pension Scheme'}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        { name: messages['common.cancel'], variant: 'outlined', onClick: toggle },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: updating,
        },
      ]}
    >
      <Descriptions column={1} bordered size="small" className="mb-4">
        <Descriptions.Item label={messages['common.type']}>
          <Tag color="cyan">{displayType}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={messages['common.name']}>
          {displayName}
        </Descriptions.Item>
        <Descriptions.Item label={messages['common.reference']}>
          {displayRef}
        </Descriptions.Item>
      </Descriptions>

      <AppFormContainer
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        initialValues={{
          eePensionType: pensionTypeOptions.find(
            (o) => o.value === data.eePensionType,
          ) || pensionTypeOptions[0],
          eePensionValue: data.eePensionValue ?? 0,
          erPensionType: pensionTypeOptions.find(
            (o) => o.value === data.erPensionType,
          ) || pensionTypeOptions[0],
          erPensionValue: data.erPensionValue ?? 0,
          avcPensionType: pensionTypeOptions.find(
            (o) => o.value === data.avcPensionType,
          ) || pensionTypeOptions[0],
          avcPensionValue: data.avcPensionValue ?? 0,
        }}
      >
        <Row gutter={24}>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.employeeContribution'] ?? 'EE Contribution'}>
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item name="eePensionType" noStyle>
                    <AppSelect options={pensionTypeOptions} className="w-full !min-w-0" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name="eePensionValue"
                    noStyle
                    dependencies={['eePensionType']}
                    rules={[privatePensionValueRule('eePensionType', messages)]}
                  >
                    {eePensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                      <AppCurrencyInput placeholder="0" />
                    ) : (
                      <AppRateInput placeholder="0" />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.employerContribution'] ?? 'ER Contribution'}>
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item name="erPensionType" noStyle>
                    <AppSelect options={pensionTypeOptions} className="w-full !min-w-0" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name="erPensionValue"
                    noStyle
                    dependencies={['erPensionType']}
                    rules={[privatePensionValueRule('erPensionType', messages)]}
                  >
                    {erPensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                      <AppCurrencyInput placeholder="0" />
                    ) : (
                      <AppRateInput placeholder="0" />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.avcContribution'] ?? 'AVC Contribution'}>
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item name="avcPensionType" noStyle>
                    <AppSelect options={pensionTypeOptions} className="w-full !min-w-0" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name="avcPensionValue"
                    noStyle
                    dependencies={['avcPensionType']}
                    rules={[privatePensionValueRule('avcPensionType', messages)]}
                  >
                    {avcPensionType?.value === EeErPensionMethodEnum.AMOUNT ? (
                      <AppCurrencyInput placeholder="0" />
                    ) : (
                      <AppRateInput placeholder="0" />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
        </Row>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default EmployeePrivatePensionEditModal;
